/*
  pro-siret-check.js
  Vérification SIREN / SIRET à l'inscription pro, côté navigateur.

  Source : API publique "Recherche d'entreprises" (DINUM / Annuaire des Entreprises).
    Endpoint  : https://recherche-entreprises.api.gouv.fr/search
    Auth      : aucune (pas de clé)
    CORS      : access-control-allow-origin: *  -> appelable depuis le thème
    Débit     : ~7 req/s/IP (large pour un formulaire)
    Pas de backend, pas d'infra à maintenir.

  Expose window.ProSiret = { verifierSiret, brancher, brancherHelium }.

  Politique de blocage recommandée côté formulaire :
    - 'ok'          -> champ validé, pré-remplissage, soumission autorisée
    - 'format'      -> bloquer la soumission (faute de frappe évidente)
    - 'introuvable' -> avertir, laisser ATLANPLAST trancher (non bloquant)
    - 'cesse'       -> avertir, laisser ATLANPLAST trancher (non bloquant)
    - 'reseau'      -> neutre, ne PAS bloquer (API momentanément indisponible)
*/

(function () {
  'use strict';

  var API = 'https://recherche-entreprises.api.gouv.fr/search';
  var TIMEOUT_MS = 5000;

  function onlyDigits(v) {
    return (v || '').replace(/\D/g, '');
  }

  function composerRue(o) {
    if (!o) return '';
    return [o.numero_voie, o.indice_repetition, o.type_voie, o.libelle_voie]
      .filter(Boolean).join(' ').replace(/\s+/g, ' ').trim();
  }

  // Clé de Luhn. Exception La Poste (SIREN 356000000) : somme multiple de 5.
  function luhnValide(num) {
    if (num.length === 14 && num.indexOf('356000000') === 0) {
      var s = 0;
      for (var k = 0; k < num.length; k++) s += +num[k];
      return s % 5 === 0;
    }
    var sum = 0, alt = false;
    for (var i = num.length - 1; i >= 0; i--) {
      var n = +num[i];
      if (alt) { n *= 2; if (n > 9) n -= 9; }
      sum += n;
      alt = !alt;
    }
    return sum % 10 === 0;
  }

  function classer(raw) {
    var d = onlyDigits(raw);
    if (d.length === 9)  return { type: 'siren', value: d, ok: luhnValide(d) };
    if (d.length === 14) return { type: 'siret', value: d, ok: luhnValide(d) };
    return { type: null, value: d, ok: false };
  }

  function appel(value) {
    var ctrl = new AbortController();
    var t = setTimeout(function () { ctrl.abort(); }, TIMEOUT_MS);
    return fetch(API + '?q=' + value + '&per_page=1', { signal: ctrl.signal })
      .then(function (res) {
        if (!res.ok) throw new Error('http_' + res.status);
        return res.json();
      })
      .finally(function () { clearTimeout(t); });
  }

  /*
    verifierSiret(raw) -> Promise<resultat>
    resultat = {
      statut: 'ok'|'format'|'introuvable'|'cesse'|'reseau',
      message: string,
      entreprise?: { siren, siret, raisonSociale, tva, naf, rue, adresse, codePostal, ville, pays },
      diffusionPartielle?: boolean
    }
  */
  function verifierSiret(raw) {
    var c = classer(raw);

    if (!c.type) {
      return Promise.resolve({
        statut: 'format',
        message: 'Numéro incomplet : 9 chiffres pour un SIREN, 14 pour un SIRET.'
      });
    }
    if (!c.ok) {
      return Promise.resolve({
        statut: 'format',
        message: 'Numéro invalide (clé de contrôle erronée). Vérifiez la saisie.'
      });
    }

    return appel(c.value).then(function (data) {
      var ul = (data.results || [])[0];
      if (!ul) {
        return { statut: 'introuvable', message: 'Aucune entreprise trouvée pour ce numéro.' };
      }

      var etab = ul.siege;
      if (c.type === 'siret') {
        var match = (ul.matching_etablissements || []).filter(function (e) {
          return e.siret === c.value;
        })[0];
        if (match) {
          etab = match;
        } else if (ul.siege && ul.siege.siret === c.value) {
          etab = ul.siege;
        } else {
          return { statut: 'introuvable', message: 'Ce SIRET ne correspond à aucun établissement connu.' };
        }
      }

      var actif = (c.type === 'siret')
        ? (etab && etab.etat_administratif === 'A')
        : (ul.etat_administratif === 'A');

      if (!actif) {
        return {
          statut: 'cesse',
          message: 'Cet établissement est administrativement fermé. Contactez-nous si c’est une erreur.'
        };
      }

      return {
        statut: 'ok',
        message: '',
        entreprise: {
          siren: ul.siren,
          siret: (etab && etab.siret) || (ul.siege && ul.siege.siret) || '',
          raisonSociale: ul.nom_raison_sociale || ul.nom_complet || '',
          tva: ul.tva || '',
          naf: (etab && etab.activite_principale) || ul.activite_principale || '',
          rue: composerRue(etab) || composerRue(ul.siege) || '',
          adresse: (etab && etab.adresse) || (ul.siege && ul.siege.adresse) || '',
          codePostal: (etab && etab.code_postal) || (ul.siege && ul.siege.code_postal) || '',
          ville: (etab && etab.libelle_commune) || (ul.siege && ul.siege.libelle_commune) || '',
          pays: 'France'
        },
        diffusionPartielle: ul.statut_diffusion && ul.statut_diffusion !== 'O'
      };
    }).catch(function () {
      return {
        statut: 'reseau',
        message: 'Vérification indisponible pour le moment. Vous pouvez soumettre, votre dossier sera vérifié.'
      };
    });
  }

  /*
    brancher(opts) : câblage générique DOM (formulaire custom hors Helium).
    opts = { champSiret, zoneMessage, prefill, debounceMs }
  */
  function brancher(opts) {
    var champ = opts.champSiret;
    var zone = opts.zoneMessage;
    var prefill = opts.prefill;
    var delay = opts.debounceMs || 500;
    var timer;

    if (!champ) return;

    champ.addEventListener('input', function () {
      clearTimeout(timer);
      var v = champ.value;
      timer = setTimeout(function () {
        var digits = onlyDigits(v);
        if (digits.length < 9) {            // champ vide ou saisie en cours : silencieux
          if (zone) { zone.textContent = ''; zone.removeAttribute('data-statut'); }
          return;
        }
        verifierSiret(v).then(function (r) {
          if (zone) {
            zone.textContent = r.message || '';
            zone.setAttribute('data-statut', r.statut);
          }
          champ.setAttribute('data-siret-valide', r.statut === 'ok' ? 'true' : 'false');
          if (r.statut === 'ok' && typeof prefill === 'function') prefill(r.entreprise);
        });
      }, delay);
    });
  }

  /*
    brancherHelium(opts) : câblage pour un formulaire Helium Customer Fields (API v4).
    Champs React-controlled : on lit via cf:field_changed, on écrit via cfForm.setFieldValue.
    opts = { formId, cleSiret, mapping{raisonSociale,tva,rue,adresse,codePostal,ville,pays}, zoneMessageId, debounceMs }
  */
  function brancherHelium(opts) {
    var formId = opts.formId;
    var cleSiret = opts.cleSiret;
    var map = opts.mapping || {};
    var delay = opts.debounceMs || 500;
    var timer;

    function onReady() {
      var $form = document.querySelector('form[data-cf-form="' + formId + '"]');
      if (!$form) return;
      var zone = opts.zoneMessageId ? document.getElementById(opts.zoneMessageId) : null;

      $form.addEventListener('cf:field_changed', function (e) {
        if (!e.detail || e.detail.columnKey !== cleSiret) return; // on ne réagit qu'au SIRET
        clearTimeout(timer);
        var val = e.detail.value;
        timer = setTimeout(function () {
          var digits = onlyDigits(val);
          if (digits.length < 9) {          // champ vide ou saisie en cours : silencieux
            if (zone) { zone.textContent = ''; zone.removeAttribute('data-statut'); }
            return;
          }
          verifierSiret(val).then(function (r) {
            if (zone) {
              zone.textContent = r.message || '';
              zone.setAttribute('data-statut', r.statut);
            }
            if (r.statut === 'ok' && r.entreprise) {
              var ent = r.entreprise;
              if (map.raisonSociale) $form.cfForm.setFieldValue(map.raisonSociale, ent.raisonSociale);
              if (map.tva && ent.tva)  $form.cfForm.setFieldValue(map.tva, ent.tva);
              if (map.rue)             $form.cfForm.setFieldValue(map.rue, ent.rue);
              if (map.adresse)         $form.cfForm.setFieldValue(map.adresse, ent.adresse);
              if (map.codePostal)      $form.cfForm.setFieldValue(map.codePostal, ent.codePostal);
              if (map.ville)           $form.cfForm.setFieldValue(map.ville, ent.ville);
              if (map.pays)            $form.cfForm.setFieldValue(map.pays, ent.pays);
            }
          });
        }, delay);
      });
    }

    if (window.CF && typeof window.CF.ready === 'function') {
      window.CF.ready(onReady);
    } else {
      document.addEventListener('cf:ready', onReady);
    }
  }

  window.ProSiret = {
    verifierSiret: verifierSiret,
    brancher: brancher,
    brancherHelium: brancherHelium
  };
})();